import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import AdminMobileMenuButton from 'components/admin/AdminMobileMenuButton';
import { Chip, Mono, Muted } from 'components/admin/clients/importConfigurations/mappings/Chips';
import Button from 'components/shared/Button';
import DataTable, { DataTableTheme } from 'components/shared/DataTable';
import Foldout from 'components/shared/Foldout';
import Checkbox from 'components/shared/form/Checkbox';
import Search from 'components/shared/Search';
import Tabs from 'components/Tabs';
import {
  adminApproveNewHospital,
  adminIndexNewHospitals,
  adminNewHospitalsQueryKeys,
  NewHospital,
  NewHospitalTab,
  newHospitalWriteError,
  useNewHospitalsCount,
} from 'services/api/admin/newHospitals';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

import { cityState, DECLINE_LABEL, filingLabel, optionForFiling, shortDate, STATUS, TABS } from './helpers';
import NewHospitalSlideout from './NewHospitalSlideout';

const STATUSES: NewHospitalTab[] = ['review', 'created', 'declined'];

const FAILURE_LABEL: Record<string, string> = {
  draft_changed: 'changed',
  hospital_exists: 'already exists',
  filing_changed: 'filing changed',
  client_has_users: 'client now has users',
};

export default function NewHospitals() {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = STATUSES.find((s) => s === searchParams.get('tab')) ?? 'review';
  const openCcn = searchParams.get('ccn');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const reviewCount = useNewHospitalsCount();

  const list = useQuery({
    queryKey: adminNewHospitalsQueryKeys.index(tab),
    queryFn: () => adminIndexNewHospitals({ status: tab }),
  });

  const listed = list.data?.find((r) => r.ccn === openCcn);
  const linked = useQuery({
    queryKey: [...adminNewHospitalsQueryKeys.all, 'find', openCcn],
    queryFn: () =>
      adminIndexNewHospitals({ ccn: openCcn! }).then((found) => found.find((r) => r.ccn === openCcn) ?? null),
    enabled: !!openCcn && !list.isLoading && !listed,
    retry: false,
  });
  const openRow = listed ?? linked.data ?? null;

  const updateParams = useCallback(
    (next: { tab?: NewHospitalTab; ccn?: string | null }) => {
      const params = new URLSearchParams(searchParams);
      if (next.tab) params.set('tab', next.tab);
      if (next.ccn === null) params.delete('ccn');
      else if (next.ccn) params.set('ccn', next.ccn);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const closeRow = useCallback(() => updateParams({ ccn: null }), [updateParams]);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: adminNewHospitalsQueryKeys.all }),
    [queryClient]
  );

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const all = list.data ?? [];
    if (!query) return all;
    return all.filter(
      (r) =>
        r.name.toLowerCase().includes(query) ||
        r.ccn.toLowerCase().includes(query) ||
        r.received.some((v) => v.key.toLowerCase().includes(query) || v.clientName.toLowerCase().includes(query))
    );
  }, [list.data, search]);

  const readyRows = useMemo(
    () => rows.filter((r) => r.status === 'ready' && optionForFiling(r.filingOptions, r.filing)),
    [rows]
  );
  const readyCcns = useMemo(() => new Set(readyRows.map((r) => r.ccn)), [readyRows]);
  const selectedReady = readyRows.filter((r) => selected.has(r.ccn));

  const toggle = (ccn: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(ccn);
      else next.delete(ccn);
      return next;
    });

  const bulkApprove = async () => {
    setBulkRunning(true);
    let created = 0;
    const failures = new Map<string, number>();
    for (const row of selectedReady) {
      try {
        await adminApproveNewHospital(row.ccn, { version: row.version, ccn: row.ccn, filing: row.filing! });
        created += 1;
      } catch (error) {
        const label = FAILURE_LABEL[newHospitalWriteError(error).code ?? ''] ?? 'failed';
        failures.set(label, (failures.get(label) ?? 0) + 1);
      }
    }
    setBulkRunning(false);
    setSelected(new Set());
    await refresh();
    const tally = [...failures].map(([label, n]) => `${n} ${label}`).join(', ');
    addToast({
      text: `Created ${created} hospital${created === 1 ? '' : 's'}.${tally ? ` Not created: ${tally}.` : ''}`,
    });
  };

  const columnHelper = createColumnHelper<NewHospital>();
  const allReadySelected = readyRows.length > 0 && readyRows.every((r) => selected.has(r.ccn));

  const columns = [
    ...(tab === 'review'
      ? [
          columnHelper.display({
            id: 'select',
            size: 40,
            header: () => (
              <Checkbox
                checked={allReadySelected}
                disabled={readyRows.length === 0}
                onChange={(on) => setSelected(on ? new Set(readyRows.map((r) => r.ccn)) : new Set())}
              />
            ),
            cell: (info) =>
              readyCcns.has(info.row.original.ccn) ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.has(info.row.original.ccn)}
                    onChange={(on) => toggle(info.row.original.ccn, on)}
                  />
                </span>
              ) : null,
          }),
        ]
      : []),
    columnHelper.accessor('name', {
      header: 'Hospital (from CMS)',
      cell: (info) => (
        <Stack>
          <Strong>{(info.row.original.createdHospital ?? info.row.original).name}</Strong>
          <Muted>
            {cityState((info.row.original.createdHospital ?? info.row.original).address)} · CCN{' '}
            <Mono>{(info.row.original.createdHospital ?? info.row.original).ccn}</Mono>
          </Muted>
        </Stack>
      ),
    }),
    columnHelper.display({
      id: 'received',
      header: 'Received as',
      cell: (info) => (
        <Stack>
          {info.row.original.received.map((r) => (
            <span key={`${r.importConfigurationId}|${r.key}`}>
              <Mono>{r.key}</Mono> <Muted>· {r.clientName}</Muted>
            </span>
          ))}
        </Stack>
      ),
    }),
    columnHelper.display({
      id: 'filing',
      header: tab === 'declined' ? 'Reason' : 'File under',
      cell: (info) => {
        const row = info.row.original;
        if (row.status === 'declined') return row.declinedReason ? DECLINE_LABEL[row.declinedReason] : '';
        if (row.status === 'pick_client' && tab === 'review') return <Muted>Needs a pick</Muted>;
        return (
          <Stack>
            {row.filing && <Muted>{row.filing.kind === 'new_client' ? 'New client' : 'Existing client'}</Muted>}
            <span>{filingLabel(row)}</span>
          </Stack>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => <Chip $tone={STATUS[info.getValue()].tone}>{STATUS[info.getValue()].label}</Chip>,
    }),
    tab === 'review'
      ? columnHelper.accessor('draftedAt', { header: 'Drafted', cell: (info) => shortDate(info.getValue()) })
      : columnHelper.accessor('decidedAt', {
          header: 'Decided',
          cell: (info) => (
            <Stack>
              <span>{shortDate(info.getValue())}</span>
              {info.row.original.decidedBy && <Muted>{info.row.original.decidedBy.name}</Muted>}
            </Stack>
          ),
        }),
  ];

  return (
    <PageContainer>
      <HeaderWrapper>
        <HeaderSection>
          <Header>
            <TitleRow>
              <AdminMobileMenuButton />
              <Title>New Hospitals</Title>
              <Muted>Hospitals the mapping agent found in CMS that aren’t in Olio yet</Muted>
            </TitleRow>
          </Header>
        </HeaderSection>
        <TabBar>
          <Tabs
            tabs={TABS.map((t) => ({
              ...t,
              affix: t.value === 'review' && !!reviewCount ? <TabBadge>{reviewCount}</TabBadge> : undefined,
            }))}
            selectedTab={TABS.find((t) => t.value === tab)}
            onTabClick={(t) => {
              setSelected(new Set());
              updateParams({ tab: t.value as NewHospitalTab });
            }}
          />
        </TabBar>
      </HeaderWrapper>

      <TableSection>
        <Toolbar>
          <SearchBox>
            <Search placeholder='Search hospitals or received names' value={search} onChange={setSearch} />
          </SearchBox>
          {tab === 'review' && (
            <Button disabled={!selectedReady.length || bulkRunning} loading={bulkRunning} onClick={bulkApprove}>
              Approve {selectedReady.length} ready
            </Button>
          )}
        </Toolbar>
        {list.isError ? (
          <Muted>New hospitals couldn’t be loaded. Reload to try again.</Muted>
        ) : !list.isLoading && rows.length === 0 ? (
          <Empty>{tab === 'review' ? 'Nothing to review.' : 'None yet.'}</Empty>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            loading={list.isLoading}
            theme={DataTableTheme.OPEN}
            onRowClick={(row) => updateParams({ ccn: row.original.ccn })}
          />
        )}
        {linked.isSuccess && !linked.data && openCcn && <Muted>No draft found for CCN {openCcn}.</Muted>}
      </TableSection>

      <Foldout open={!!openRow} position='right' width='min(90%, 600px)' onBackdropClick={closeRow}>
        {openRow && (
          <NewHospitalSlideout
            key={`${openRow.ccn}|${openRow.version}`}
            row={openRow}
            onClose={closeRow}
            onDone={() => {
              closeRow();
              refresh();
            }}
            onReload={refresh}
          />
        )}
      </Foldout>
    </PageContainer>
  );
}

const PageContainer = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: auto;
`;

const HeaderWrapper = styled.div`
  background: white;
`;

const HeaderSection = styled.div`
  padding: 24px 24px 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 16px;
  min-width: 0;
`;

const TabBar = styled.div`
  padding: 0 24px;
  border-top: 1px solid ${colors.black10};
`;

const TabBadge = styled.span`
  display: inline-block;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 8px;
  background: ${colors.red100};
  color: white;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
`;

const TableSection = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  table {
    font-size: 14px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const SearchBox = styled.div`
  flex: 0 1 320px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Strong = styled.span`
  font-weight: 500;
`;

const Empty = styled.p`
  font-size: 14px;
  color: ${colors.black50};
  margin: 0;
`;
